/**
 * Analytics feature documentation page
 */

import {DocsContent} from '../../../components/docs/DocsContent';
import {CodeBlock} from '../../../components/docs/CodeBlock';
import {DocsActionButtons} from '../../../components/docs/DocsActionButtons';
import {Link} from 'react-router-dom';

export function Analytics() {
  return (
    <DocsContent
      title="Analytics"
      description="Track meeting metrics, engagement, and productivity insights."
    >
      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Overview</h2>
          <p className="text-textSecondary mb-4">
            VoxaPilot provides comprehensive analytics to help you understand meeting patterns,
            participation, and productivity. Track metrics across individual meetings or your entire
            organization.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Meeting Metrics</h2>
          <p className="text-textSecondary mb-4">For each meeting, VoxaPilot tracks:</p>

          <ul className="list-disc list-inside space-y-2 text-textSecondary mb-6">
            <li>Duration and participant count</li>
            <li>Speaking time per participant</li>
            <li>Number of contributions per speaker</li>
            <li>Topics discussed and time spent on each</li>
            <li>Sentiment analysis (positive, neutral, negative)</li>
            <li>Engagement levels</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Organization Analytics</h2>
          <p className="text-textSecondary mb-4">
            For teams and organizations, VoxaPilot provides aggregated analytics:
          </p>

          <ul className="list-disc list-inside space-y-2 text-textSecondary mb-6">
            <li>Total meeting hours per week/month</li>
            <li>Average meeting duration</li>
            <li>Most active participants</li>
            <li>Meeting frequency trends</li>
            <li>Productivity insights</li>
            <li>Cost analysis (for paid plans)</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Accessing Analytics</h2>
          <p className="text-textSecondary mb-4">
            View analytics through the dashboard or API:
          </p>

          <CodeBlock
            code={`// Get meeting analytics
const analytics = await api.analytics.getMeetingAnalytics(meetingId);

console.log('Duration:', analytics.duration);
console.log('Participants:', analytics.participants);
console.log('Speaking Time:', analytics.speakingTime);
console.log('Sentiment:', analytics.sentiment);

// Get organization analytics
const orgAnalytics = await api.analytics.getOrganizationAnalytics({
  startDate: '2024-01-01',
  endDate: '2024-01-31',
});

console.log('Total Hours:', orgAnalytics.totalHours);
console.log('Average Duration:', orgAnalytics.averageDuration);
console.log('Top Participants:', orgAnalytics.topParticipants);`}
            language="javascript"
          />
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Exporting Data</h2>
          <p className="text-textSecondary mb-4">
            Export analytics data for further analysis:
          </p>

          <ul className="list-disc list-inside space-y-2 text-textSecondary mb-6">
            <li>CSV export for spreadsheet analysis</li>
            <li>JSON export for programmatic access</li>
            <li>Scheduled reports via email</li>
            <li>API access for custom integrations</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Next Steps</h2>
          <ul className="list-disc list-inside space-y-2 text-textSecondary dark:text-[#858585]">
            <li>
              <Link to="/docs/api/endpoints" className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
                Use the analytics API
              </Link>
            </li>
            <li>
              <Link to="/docs/configuration/settings" className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
                Configure analytics settings
              </Link>
            </li>
          </ul>
        </section>

        <DocsActionButtons pageTitle="Analytics" />
      </div>
    </DocsContent>
  );
}

